import { cloneDeep } from 'lodash';

/**
 * Combination identity for Landed Information rows on the Product Costing detail page (BM-9220).
 *
 * Pure: no React, no redux, no I/O — the same code runs in the page and in the tests.
 *
 * Two row shapes meet here. Rows loaded from a saved costing carry `colourway` / `length` as
 * populated documents, because that is how ProductCosting persists them. Rows rebuilt by the costing
 * template endpoint carry `colourWay` / `length` plus the scalar `colourWayId` / `lengthId`, because
 * that endpoint derives them from the development order at request time. Both shapes must key the
 * same way for a saved combination to be recognised in a freshly built set.
 */

/** A populated reference as it reaches the client — the id has already been serialised to a string. */
export interface CombinationRef {
  _id?: string;
  name?: string;
  [key: string]: unknown;
}

/** One Landed Information row. Only the combination identity is typed; every other column passes through. */
export interface LandedCombinationRow {
  colourway?: CombinationRef | null;
  colourWay?: CombinationRef | null;
  colourWayId?: string | null;
  size?: string | null;
  length?: CombinationRef | null;
  lengthId?: string | null;
  [key: string]: unknown;
}

/** One entry of `landedInfoStates` — a distribution centre and the landed rows held against it. */
export interface LandedDistributionCentreState {
  landedInfo?: { landedInfoArr?: LandedCombinationRow[] };
  [key: string]: unknown;
}

/**
 * Builds the combination identity of a Landed Information row.
 *
 * `size` is trimmed on purpose. ProductCosting declares `size` with `trim: true` while
 * DevelopmentOrder declares `sizeRanges` as a plain string array, so a product size entered as `'M '`
 * reaches a saved costing as `'M'` but comes back from the template endpoint still as `'M '`. Without
 * the trim those two rows key differently and no saved combination would ever be recognised.
 *
 * @param row a saved or template-built landed row.
 * @returns `colourwayId|size|lengthId`, with an empty segment wherever that axis is not in play.
 */
export function buildLandedCombinationKey(row: LandedCombinationRow | null | undefined): string {
  const colourwayId = row?.colourway?._id || row?.colourWay?._id || row?.colourWayId || '';
  const lengthId = row?.length?._id || row?.lengthId || '';
  const size = String(row?.size ?? '').trim();
  return `${colourwayId}|${size}|${lengthId}`;
}

/**
 * Picks the combination set a costing was saved with.
 *
 * The first distribution centre that actually holds rows wins. A centre added after the costing was
 * saved carries an empty array of its own, and it has to follow the saved centres rather than fall
 * back to the development order's current colourways, sizes and lengths.
 *
 * @param dcStates the `landedInfoStates` entries, saved or freshly scaffolded.
 * @returns the saved rows, or an empty array when nothing has been saved yet.
 */
export function getSavedLandedCombinationRows(dcStates: LandedDistributionCentreState[] | null | undefined): LandedCombinationRow[] {
  const savedCentre = (dcStates || []).find((dcState) => (dcState?.landedInfo?.landedInfoArr?.length ?? 0) > 0);
  return savedCentre?.landedInfo?.landedInfoArr ?? [];
}

/**
 * Restricts freshly built template rows to the combinations a costing was saved with.
 *
 * Driven from the saved rows rather than by filtering the template rows, so the result always carries
 * the saved set's length and order. A saved combination the development order no longer offers keeps
 * its row, rebuilt on the new template's columns; that also means the result can never come back
 * empty, which matters because an empty Landed Information array is persisted verbatim by the next
 * save.
 *
 * With nothing saved — creating a costing, or a saved costing that has no landed rows — the template
 * rows are returned untouched, so the create flow is unaffected.
 *
 * @param templateRows rows the costing template endpoint built from the development order.
 * @param savedRows the landed rows the costing was saved with.
 * @returns template rows narrowed to the saved combinations.
 */
export function restrictRowsToSavedCombinations(
  templateRows: LandedCombinationRow[] | null | undefined,
  savedRows: LandedCombinationRow[] | null | undefined,
): LandedCombinationRow[] {
  if (!Array.isArray(templateRows) || templateRows.length === 0) return templateRows ?? [];
  if (!Array.isArray(savedRows) || savedRows.length === 0) return templateRows;

  const templateRowsByKey = new Map(templateRows.map((row) => [buildLandedCombinationKey(row), row]));
  const columnPrototype = templateRows[0];

  return savedRows.map((savedRow) => {
    const matchedRow = templateRowsByKey.get(buildLandedCombinationKey(savedRow));
    if (matchedRow) return cloneDeep(matchedRow);
    return {
      ...cloneDeep(columnPrototype),
      colourWay: savedRow?.colourway || savedRow?.colourWay || null,
      colourWayId: savedRow?.colourway?._id || savedRow?.colourWay?._id || savedRow?.colourWayId || null,
      size: savedRow?.size || '',
      length: savedRow?.length || null,
      lengthId: savedRow?.length?._id || savedRow?.lengthId || null,
    };
  });
}
